import { TrendingUp, TrendingDown, Minus, Target, Zap } from 'lucide-react';

export default function McKeeView({ analysis, onBeatSelect, selectedBeat }) {
  if (!analysis || !analysis.valueProgression) {
    return (
      <div className="text-center py-8 text-slate-500">
        <Target className="w-12 h-12 mx-auto mb-3 text-slate-300" />
        <p>No McKee Structure analysis available</p>
      </div>
    );
  }

  const { valueProgression } = analysis;

  const getValueIcon = (arcType) => {
    if (arcType === 'positive') return <TrendingUp className="w-4 h-4 text-green-500" />;
    if (arcType === 'negative') return <TrendingDown className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-slate-500" />;
  };

  const getValueColor = (arcType) => {
    if (arcType === 'positive') return 'text-green-600 bg-green-50 border-green-200';
    if (arcType === 'negative') return 'text-red-600 bg-red-50 border-red-200';
    return 'text-slate-600 bg-slate-50 border-slate-200';
  };

  return (
    <div className="space-y-8">
      {/* Value Progression Overview */}
      <div className="bg-orange-50 rounded-lg p-6 border border-orange-200">
        <h3 className="text-lg font-semibold text-orange-900 mb-4 flex items-center">
          <Zap className="w-5 h-5 mr-2" />
          Story Value Progression
        </h3>
        <p className="text-sm text-orange-700 mb-4">
          <strong>Value Progression:</strong> Tracks how the protagonist's values or circumstances change from positive to negative or vice versa throughout the story. Strong narratives show clear value shifts at major turning points.
        </p>

        {/* Simple Visual Arc */}
        <div className="bg-white rounded-lg p-6 border border-orange-300">
          <div className="flex justify-between items-center">
            {valueProgression.map((act, index) => (
              <div key={index} className="flex-1 text-center">
                <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-2 ${
                  act.arcType === 'positive' ? 'bg-green-100' :
                  act.arcType === 'negative' ? 'bg-red-100' : 'bg-slate-100'
                }`}>
                  {getValueIcon(act.arcType)}
                </div>
                <div className="font-semibold text-slate-900">Act {act.act}</div>
                <div className="text-xs text-slate-500 mt-1">
                  {act.arcType} arc
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6 flex items-center justify-between">
            {valueProgression.map((act, index) => (
              <div key={index} className="flex-1">
                {index < valueProgression.length - 1 && (
                  <div className="h-1 bg-orange-300 mx-4"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Act-by-Act Breakdown */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold flex items-center">
          <Target className="w-5 h-5 mr-2 text-orange-600" />
          McKee's Value Structure
        </h3>
        {valueProgression.map((act) => (
          <div key={act.act} className="bg-white border rounded-lg p-6 hover:shadow-md transition-shadow">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h4 className="text-xl font-semibold text-slate-900">Act {act.act}</h4>
                <p className="text-sm text-slate-500 mt-1">
                  {act.act === 1 && 'Setup & Inciting Incident'}
                  {act.act === 2 && 'Rising Action & Complications'}
                  {act.act === 3 && 'Climax & Resolution'}
                </p>
              </div>
              <div className="flex items-center space-x-3">
                {getValueIcon(act.arcType)}
                <span className={`px-3 py-1 rounded border font-medium capitalize ${getValueColor(act.arcType)}`}>
                  {act.arcType}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-slate-50 p-4 rounded">
                <h5 className="font-medium text-slate-900 mb-2">Starting Value</h5>
                <p className="text-sm text-slate-700">{act.startValue}</p>
              </div>
              <div className="bg-slate-50 p-4 rounded">
                <h5 className="font-medium text-slate-900 mb-2">Ending Value</h5>
                <p className="text-sm text-slate-700">{act.endValue}</p>
              </div>
            </div>

            {act.keyScenes && act.keyScenes.length > 0 && (
              <div className="mt-4">
                <h5 className="font-medium text-slate-900 mb-2">Key Scenes</h5>
                <ul className="space-y-1">
                  {act.keyScenes.map((scene, idx) => (
                    <li key={idx} className="text-sm text-slate-700 flex items-start">
                      <span className="text-orange-500 mr-2">•</span>
                      <span>{scene}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Theory Explanation */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h4 className="font-semibold text-blue-900 mb-3">📚 About McKee's Value Progression</h4>
        <div className="text-sm text-blue-800 space-y-2">
          <p>
            <strong>Robert McKee's approach</strong> focuses on tracking how core values shift throughout your story.
            Values can be love/hate, life/death, success/failure, justice/injustice, etc.
          </p>
          <p>
            <strong>Strong stories</strong> show clear progression where each act drives the protagonist toward or away
            from their goal, with Act 2 typically containing the most dramatic reversals.
          </p>
          <p>
            <strong>Look for:</strong> Are your value shifts clear and meaningful? Does each act build on the previous one?
            Is there a satisfying resolution to the value at stake?
          </p>
        </div>
      </div>
    </div>
  );
}
